import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import moment from "moment";
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils";

export const premiseUserList = createAsyncThunk('premise_user/list', async ({ pageable: data, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `premise_user`,
            params: {
                'pageSize': data?.pageSize,
                'pageNo': data?.currentPage,
                'search': data?.search,
                'premise_id': data?.premise_id,
                'role_id': data?.role_id,
                'is_staff': data?.is_staff,
                'admin': data?.admin,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const premiseUserDetails = createAsyncThunk('premise_user/details', async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `premise_user/${id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addPremiseUser = createAsyncThunk('premise_user/add', async (data) => {

    var bodyFormData = new FormData()

    bodyFormData.append("premise_id", data?.values?.premise_id)
    bodyFormData.append("role_id", data?.values?.role_id)
    bodyFormData.append("first_name", data?.values?.fname)
    bodyFormData.append("last_name", data?.values?.lname)
    bodyFormData.append("gender", data?.values?.gender)
    bodyFormData.append("user_name", data?.values?.username)
    bodyFormData.append("password", data?.values?.password)
    bodyFormData.append("user_mobile", data?.values?.mobile)
    bodyFormData.append("user_email", data?.values?.email)
    bodyFormData.append("dob", data?.values?.dob && moment(data?.values?.dob, 'DD/MM/YYYY').format('YYYY-MM-DD'))

    if (data?.values?.is_staff) {
        bodyFormData.append("joining_date", data?.values?.joining_date && moment(data?.values?.joining_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
        bodyFormData.append("salary_amount", data?.values?.salary)
    }

    if (data?.values?.profile) {
        bodyFormData.append("user_profile", data.values.profile)
    }

    bodyFormData.append("status", data?.values?.status)

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'premise_user',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "profile", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updatePremiseUser = createAsyncThunk('premise_user/update', async (data) => {

    var bodyFormData = new FormData()

    bodyFormData.append("user_id", data?.values?.id)
    bodyFormData.append("premise_id", data?.values?.premise_id)
    bodyFormData.append("role_id", data?.values?.role_id)
    bodyFormData.append("first_name", data?.values?.fname)
    bodyFormData.append("last_name", data?.values?.lname)
    bodyFormData.append("gender", data?.values?.gender)
    bodyFormData.append("user_name", data?.values?.username)
    if (data?.values?.password) {
        bodyFormData.append("password", data?.values?.password)
    }
    bodyFormData.append("user_mobile", data?.values?.mobile)
    bodyFormData.append("user_email", data?.values?.email)
    bodyFormData.append("dob", data?.values?.dob && moment(data?.values?.dob, 'DD/MM/YYYY').format('YYYY-MM-DD'))

    if (data?.values?.is_staff) {
        bodyFormData.append("joining_date", data?.values?.joining_date && moment(data?.values?.joining_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
        bodyFormData.append("salary_amount", data?.values?.salary)
    }

    if (data?.values?.profile) {
        bodyFormData.append("user_profile", data.values.profile)
    }

    bodyFormData.append("status", data?.values?.status)

    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'update_premise_user',
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", "type": "profile", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updatePremiseUserMfa = createAsyncThunk('premise_user/updateMfa', async (data) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'premise_user_mfa',
            data: {
                user_id: data?.id,
                user_mfa: data?.user_mfa
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") }
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const delPremiseUser = createAsyncThunk('premise_user/delete', async (data) => {
    try {
        const res = await axios({
            method: 'DELETE',
            url: getConfig.API_URL + `premise_user/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie('user_token') }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const premiseUser = createSlice({
    name: 'premise_user',
    initialState: {
        list: [],
        details: null,
        loading: false,
    },
    reducers: {
        removePremiseUser: (state, action) => {
            state.list = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(premiseUserList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(premiseUserList.fulfilled, (state, action) => {
            state.loading = false;
            if (action?.payload?.status === 200) {
                state.list = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        });
        builder.addCase(premiseUserList.rejected, (state, action) => {
            state.loading = false;
        })

        builder.addCase(premiseUserDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        });
    }
})

export const { removePremiseUser } = premiseUser.actions;
export default premiseUser.reducer;